/**
 * `HeightTransition` — animates REAL height changes of its content
 * (the few legitimate layout moves: accordions, notes, expanding
 * detail) instead of letting them snap. Everything else in the motion
 * system is compositor-only (plan §1.2); this is the one explicit
 * escape hatch for measured height.
 *
 * Mechanism: a ResizeObserver measures the inner content; the outer
 * wrapper animates its `height` style between measurements
 * (`MOTION.durSlow` / `easeInOut`). The FIRST measurement applies
 * without animating (no entrance jump).
 *
 * Reduced motion (or no ResizeObserver — old jsdom): renders the
 * content directly with natural height, instant changes.
 */
import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import {
  MOTION,
  usePrefersReducedMotion,
} from "@pervasivemind/bi-components";

export interface HeightTransitionProps {
  children: ReactNode;
  /** Transition duration in ms. Defaults to `MOTION.durSlow`. */
  durationMs?: number;
  className?: string;
}

export function HeightTransition({
  children,
  durationMs = MOTION.durSlow,
  className,
}: HeightTransitionProps): JSX.Element {
  const reduced = usePrefersReducedMotion();
  const innerRef = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState<number | null>(null);
  // First measurement is applied without transition.
  const measuredOnceRef = useRef(false);

  const observerAvailable =
    typeof ResizeObserver !== "undefined";
  const animate = !reduced && observerAvailable;

  useEffect(() => {
    if (!animate) return undefined;
    const el = innerRef.current;
    if (el === null) return undefined;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const h = entry.target instanceof HTMLElement
          ? entry.target.offsetHeight
          : entry.contentRect.height;
        setHeight(h);
        // Flip AFTER the first measurement has been committed.
        requestAnimationFrame(() => {
          measuredOnceRef.current = true;
        });
      }
    });
    ro.observe(el);
    setHeight(el.offsetHeight);
    return () => {
      ro.disconnect();
      measuredOnceRef.current = false;
    };
  }, [animate]);

  if (!animate) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div
      className={className}
      data-pm-height-transition
      style={{
        height: height ?? undefined,
        overflow: "hidden",
        transition: measuredOnceRef.current
          ? `height ${durationMs}ms ${MOTION.easeInOut}`
          : undefined,
      }}
    >
      <div ref={innerRef}>{children}</div>
    </div>
  );
}
